"use client"
import { useLayoutEffect, useRef, useState } from "react"

// How many list rows a hover detail's table can paint between the table's
// top and the bottom of the screen, minus a reserve for whatever must stay
// visible below the table (the "Click for details" hint, breathing room).
// Shared by every hover detail with a viewport-capped table (the datacenter
// list, the power breakdown columns) so they all truncate on one rule.
//
// Attach `containerRef` to an element enclosing the table and pass a
// `rowSelector` matching one data row inside it — the first match is the
// probe that supplies both the rows' top edge and the per-row height.
// Returns null until measured; render every row on the first paint — the
// pre-paint layout effect trims before anything flashes.
export const useHoverDetailViewportRowCap = ({
  enabled,
  rowSelector,
  bottomReservePx,
  remeasureKey,
}: {
  enabled: boolean
  rowSelector: string
  bottomReservePx: number
  // Change this when the table's content changes (row count, story date) so
  // the cap re-measures against the new layout.
  remeasureKey?: unknown
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [rowCap, setRowCap] = useState<number | null>(null)
  useLayoutEffect(() => {
    if (!enabled) return
    const measure = () => {
      const row = containerRef.current?.querySelector<HTMLElement>(rowSelector)
      if (!row) return
      const rowBox = row.getBoundingClientRect()
      if (rowBox.height <= 0) return
      const available = window.innerHeight - bottomReservePx - rowBox.top
      setRowCap(Math.max(1, Math.floor(available / rowBox.height)))
    }
    measure()
    window.addEventListener("resize", measure)
    return () => window.removeEventListener("resize", measure)
  }, [enabled, rowSelector, bottomReservePx, remeasureKey])
  return { containerRef, rowCap }
}
